/*!
 * mm-analysis.js — the page side of the mashup maker's analysis: ONE scheduler over a small pool
 * of analysis workers (mm-worker.js).
 *
 *   var J = MashupAnalysis.job({ prio })   — the work for one song. prio 0 = the build (a person
 *                                            watches its bar), 1 = a deck, 2 = the pair finder
 *   J.beats(audioBuffer, { onProgress(done, of), keepActs }) → grid (mm-grid.js shape)
 *   J.mixFeat(audioBuffer)                 → { profile, key, tuningCents, rms }
 *   J.park() / J.resume()                  — nobody needs the song NOW (its slot was emptied): it
 *                                            yields to every other song and is dropped after
 *                                            PARK_MS, unless its file comes back (picked again)
 *   J.cancel()                             — queued work dropped, its promises reject AbortError
 *   MashupAnalysis.beats / mixFeat / vocalFeat / bandFeat — one-off jobs
 *   MashupAnalysis.ensure() → { provider, model }, .info(), .release(), .onModelProgress(fn),
 *   .chunksFor(seconds) — how many model chunks a song of that length is (the page's progress)
 *
 * Why a scheduler (bug report 2026-09-27, /ru/mashup-maker, Android 10, 4 GB, no WebGPU: «вокал и
 * минус загружены, кнопка неактивна, прошло около 30 минут»). The analysis was ONE worker taking
 * whole songs in arrival order:
 *   · a song removed and picked again was analysed TWICE — the first run kept the worker for
 *     minutes after nobody needed it and the second queued behind it (25 minutes on that phone);
 *   · a pair-finder song queued between a deck's beats and its key, and a deck whose model run was
 *     over waited for the OTHER deck's whole model run to get its 1-second key (measured: song B
 *     ready 99 s after its own work was done);
 *   · one core of eight worked, and the worker kept ~700 MB for the life of the page: Beat This!'s
 *     frontend attends along time per frequency band — a 32 × T × T float tensor in each of three
 *     blocks, 288 MB at T = 1500 (graph shapes; measured +777 MB RSS per worker, returned only by
 *     terminate()).
 * Now the page cuts a song's log-mel into model CHUNKS and hands them out by (priority, song,
 * order): several WASM workers take chunks of the same song side by side where the device has
 * the cores and the memory, a parked song yields, a cancelled one never reaches a worker, and an
 * idle pool is terminated so the build gets its memory back.
 *
 * Chunk length on WASM is 1000 frames, not the model's native 1500 (measured 2026-09-27 on 10
 * songs against the final0 oracle): beats F 0.925 / downbeats 0.789 against 0.923 / 0.787, grids
 * agreeing 0.991 / 0.918, peak memory 612 MB against 1 315 (native ORT) — half a worker, which is
 * what lets a 4 GB phone run two. 750 and 500 were rejected: each broke a song (beats F 0.49 on
 * one; a doubled tempo on another). A graphics card keeps 1500: its memory is not the wasm heap.
 *
 * Which beat model: the 39.5 MB final0 on a graphics card the ladder has verified (3.5–4.8 s a
 * song), otherwise the 4.9 MB small0 on WASM. A card that answers the known rhythm WRONG is
 * remembered for 30 days (OrtEpVerify on the page) and not asked again.
 *
 * Every piece of work here ENDS (bug report 2026-10-06, /ru/audio-to-notes, Opera, Windows 10,
 * Intel gen-9 graphics: «остановилось на десяти процентах не скачать ноты» — the page waited 64
 * minutes for a chunk that could not come back). Reproduced with CDP Browser.crashGpuProcess: a
 * card the browser loses under a chunk leaves ORT's run pending for ever, nothing here noticed,
 * and the song's promise never settled. Measured in the field, 02–06.10.2026, 260 sessions that
 * ran the beats on a card (/audio-to-notes): the card was lost in 13 of them — 12 of the 13 on
 * cards that had needed 15 s or more to become ready (16 % of those 74 sessions; 1 of 186 below
 * 15 s); the reporter's own card lost the song on four days out of four. Three things, each with
 * its own evidence:
 *   · the card says it is gone — the worker answers the run for it (mm-worker.js), the page takes
 *     the card out and the song runs again, by itself, on the processor (`model_switched` inside
 *     beatsOf — the caller sees one song that took longer);
 *   · a worker that does not answer a job is silent, not slow — QUIET, counted in VISIBLE time
 *     (a phone freezes a background tab together with its workers). The floors sit above
 *     everything the field has shown (/mashup-maker, 28.09–06.10, 1 073 songs, the mean chunk of a
 *     song: WASM p99 52 s, max 70 s; a card p99 13 s, max 60 s) and grow with what a chunk took on
 *     THIS device;
 *   · a card that is slow to START is not given the song at all — READY, in ACTIVE time (the
 *     device's own tempo). By how long the card took after its bytes were in hand, model seconds
 *     per minute of song (same /mashup-maker songs): under 5 s — 1.0; 5–10 s — 3.0; 10–15 s — 3.8;
 *     15–30 s — 6.3; 30 s and over — 21.2, against 8.0 for a computer's processor. Past 30 s the
 *     card is 2.6× SLOWER than the processor it was chosen over, and that is where it is lost.
 * A card lost or silent under a song is remembered for 30 days like one that answered wrong: on
 * the reporter's machine every visit began with the same loss. A slow start is not remembered —
 * it is measured again on the next visit.
 *
 * URLs come from meta tags the blade prints (the worker form belongs to AssetRouter, the model
 * routing to heavy()); nothing here writes a path.
 */
(function (root) {
    'use strict';

    var CHUNK_WASM = 1000, CHUNK_GPU = 1500, BORDER = 6, NMEL = 128;
    var PARKED = 9, PARK_MS = 90000;
    // ms without an answer after which a job's worker is silent (visible time), and how many learned
    // chunk times the floor grows to on a device that is honestly slow
    var QUIET = { gpu: 60000, wasm: 180000, dsp: 300000, scale: 8 };
    // ms for a model to become ready once its bytes are in hand: the card — its tempo (active time),
    // the processor — a start that never ends (visible time; the field's p95 is 23 s, the slowest seen 204 s)
    var READY = { webgpu: 30000, wasm: 300000 };

    var pool = [], queue = [], taskSeq = 0, jobSeq = 0, workerSeq = 0;
    var ready = null, readyInfo = null, modelOk = false, modelBytes = null, idleTimer = null, extraFails = 0, songsInWork = 0;
    // this page no longer asks the card: it was slow to start, went silent, was lost or answered wrong
    var gpuOff = false;

    function meta(name) { var m = document.querySelector('meta[name="' + name + '"]'); return m ? m.getAttribute('content') : ''; }
    function isWebKit() { try { return !!(root.__ortBuild && root.__ortBuild.wasmOnly()); } catch (_) { return false; } }
    function abortError() { var e = new Error('cancelled'); e.name = 'AbortError'; return e; }
    function codedError(m) { return Object.assign(new Error(m.message || m.code || 'worker error'), { code: m.code, verdicts: m.verdicts }); }
    function switched() { return Object.assign(new Error('model switched'), { code: 'model_switched' }); }
    function tell(name, data) { try { root.__aeFeature && root.__aeFeature(name, data); } catch (_) {} }
    var REJ_KEY = 'mm-beat-webgpu';
    // what a worker says when its card is gone (mm-worker.js) → why the card is taken out: `lost` is
    // the device's own word, `rejected` — a run that failed in a promise nobody awaited
    var GPU_GONE = { gpu_device_lost: 'lost', gpu_run_rejected: 'rejected' };

    // ── clocks ───────────────────────────────────────────────────────────────
    /** fn after `ms` of VISIBLE time (timbrica-core.js); → cancel. A hidden tab is not a silent worker. */
    function afterVisible(fn, ms) {
        if (typeof root.__visibleTimeout === 'function') return root.__visibleTimeout(fn, ms);
        var h = setTimeout(fn, ms); return function () { clearTimeout(h); };
    }
    function visibleNow() { return typeof root.__visibleNow === 'function' ? root.__visibleNow() : performance.now(); }
    /** The time the page could actually work (timbrica-core.js): a frozen tab did not make the card slow. */
    function activeNow() { return typeof root.__activeNow === 'function' ? root.__activeNow() : performance.now(); }
    /** fn once `ms` of ACTIVE time have passed; → cancel. */
    function afterActive(fn, ms) {
        var t0 = activeNow(), h = 0, off = false;
        (function wait() {
            if (off) return;
            var left = ms - (activeNow() - t0);
            if (left <= 0) { off = true; fn(); return; }
            // a timer runs on the wall clock: it only says when to look at the active clock again
            h = setTimeout(wait, Math.max(20, Math.min(500, left)));
        })();
        return function () { off = true; clearTimeout(h); };
    }

    function nav() { return root.navigator || {}; }
    /** A phone or a tablet — also one in «desktop site» mode, whose user agent says Linux x86_64
     *  (device-capabilities.js isHandheld; bug report 2026-09-28: sized as a computer, it fetched
     *  the 41 MB graphics-card model for a phone with no adapter). */
    function isPhone() {
        try { var c = root.__deviceCap && root.__deviceCap.get ? root.__deviceCap.get() : null; if (c) return !!(c.isMobile || c.isHandheld); } catch (_) {}
        var n = nav(), ua = n.userAgent || ''; return /Mobi|Android|iPhone|iPad/i.test(ua) || (/Macintosh/.test(ua) && n.maxTouchPoints > 1);
    }
    function lowMemory() { var dm = nav().deviceMemory; return typeof dm === 'number' ? dm <= 4 : isPhone(); }

    /**
     * How many WASM workers may take chunks at once. One core stays with the page (the decoder, the
     * audio, the screen). Memory, measured 2026-09-27 (browser tree RSS, Chrome, small0): a worker
     * holds 423 MB at 1000-frame chunks against 736 MB at 1500, at the same speed per frame — so two
     * workers on a 4 GB device cost what the ONE worker of before did (777 MB) and are done in half the
     * time, and the pool is terminated after the listening instead of living with the page. A device
     * that does not say its memory (Safari) gets one on a phone. A graphics card is one queue: one worker.
     */
    function maxWorkers() {
        if (!readyInfo || readyInfo.provider !== 'wasm') return 1;
        var n = nav(), hc = n.hardwareConcurrency || 2, dm = n.deviceMemory;
        var byCores = Math.max(1, Math.min(4, hc - 1));
        var byMem = typeof dm === 'number' ? (dm < 4 ? 1 : dm < 8 ? 2 : 4) : (isPhone() ? 1 : 2);
        // a phone's tab is killed long before a computer's: two at most (four are ~1.9 GB), whatever
        // the phone reports (Chrome says 8 for anything from 8 GB up)
        if (isPhone()) byMem = Math.min(byMem, 2);
        return Math.max(1, Math.min(byCores, byMem, policy.workerCap || 4));
    }
    /**
     * A page that runs OTHER heavy work beside the beats caps the pool once, before the first job
     * (/audio-to-notes: Basic Pitch transcribes in its own worker while the beats are tracked).
     *   workerCap  — at most this many analysis workers (each holds ~200 MB of the tab)
     *   gpuReadyMs — the card gets the song only if it became ready within this long. /audio-to-notes
     *                asks for 15 s: beside a transcription a card that needed 15–30 s was lost in 4
     *                sessions of 40 and took as long as the processor does there (its songs p50 58 s
     *                with the start, a computer's processor 40 s), and 30 s would have let through two
     *                of the reporter's five starts — both of them lost
     * Not calling it keeps the mashup maker's own sizing and the 30 s the header explains.
     */
    var policy = { workerCap: 0, gpuReadyMs: 0 };
    function configure(o) {
        o = o || {};
        if (o.workerCap != null) policy.workerCap = Math.max(1, o.workerCap | 0);
        if (o.gpuReadyMs != null) policy.gpuReadyMs = Math.max(1000, o.gpuReadyMs | 0);
    }
    // test seams, read at the moment of use: window.__MM_QUIET_MS / __MM_READY_MS (a number for every
    // kind) shorten the deadlines for a stand
    function readyBudget(provider) {
        var o = +root.__MM_READY_MS; if (o > 0) return o;
        return provider === 'webgpu' ? (policy.gpuReadyMs || READY.webgpu) : READY.wasm;
    }
    /** How long a job may stay unanswered on this worker before the worker is silent. */
    function quietBudget(rec, t) {
        var o = +root.__MM_QUIET_MS; if (o > 0) return o;
        if (!t.session) return Math.max(QUIET.dsp, QUIET.scale * (took[t.type] || 0));
        return Math.max(rec.gpu ? QUIET.gpu : QUIET.wasm, QUIET.scale * (took[t.type] || 0));
    }

    // ── the verdicts a worker reports (the ladder at init, the known rhythm after a song) ──
    function onVerdict(m) {
        try {
            var V = root.OrtEpVerify;
            // only a WRONG ANSWER is remembered — a provider that threw never answered anything
            if (V && m.verdict && m.verdict.provider === 'webgpu' && m.verdict.ok === false && !m.verdict.error && V.remember) V.remember(REJ_KEY, 'webgpu');
        } catch (_) {}
        if (m.verdict && m.verdict.pooled) return;
        // `err` — a provider that THREW (its session did not even create): without ORT's own words a
        // reservation failure and a missing SIMD read the same «ok:0», and refund #43 was unexplainable
        tell('mm_beat_verdict', { ok: m.verdict && m.verdict.ok ? 1 : 0, p: m.verdict && m.verdict.provider, fb: m.verdict && m.verdict.fBeat, fd: m.verdict && m.verdict.fDown, ms: m.verdict && m.verdict.ms,
            err: m.verdict && m.verdict.error ? String(m.verdict.error).slice(0, 100) : undefined });
    }

    // ── workers ──────────────────────────────────────────────────────────────
    function spawn(kind, model) {
        var url = meta('mm-worker-url');
        if (!url) throw Object.assign(new Error('worker url missing'), { code: 'not_wired' });
        var rec = { id: ++workerSeq, w: new Worker(url), kind: kind, session: kind !== 'bare', ready: kind === 'bare', dead: false, busy: null, init: null,
            // the model this worker runs ('final0/webgpu', 'small0/wasm'; none for a bare one), known from its start
            model: model || null, gpu: /\/webgpu$/.test(model || ''), quiet: null };
        rec.w.onmessage = function (ev) { onWorkerMessage(rec, ev.data || {}); };
        rec.w.onerror = function (e) { onWorkerDeath(rec, e); };
        pool.push(rec);
        return rec;
    }
    function disarm(rec) { if (rec.quiet) { var c = rec.quiet; rec.quiet = null; try { c(); } catch (_) {} } }
    /**
     * The job a worker holds gets one deadline, from the moment it was sent to its answer. Nothing
     * re-arms it: during a WASM run the worker cannot say anything, and a card's worker that still
     * talks is exactly the case this is for — the worker alive, its run gone with the card.
     */
    function armQuiet(rec, t) {
        disarm(rec);
        var ms = quietBudget(rec, t), v0 = visibleNow(), w0 = performance.now();
        rec.quiet = afterVisible(function () {
            rec.quiet = null;
            if (rec.dead || rec.busy !== t || t.settled) return;
            tell('mm_beat_silent', { type: t.type, p: rec.gpu ? 'webgpu' : rec.session ? 'wasm' : 'dsp', kind: rec.kind, ms: ms,
                hid: Math.max(0, Math.round((performance.now() - w0) - (visibleNow() - v0))), took: Math.round(took[t.type] || 0) });
            if (rec.gpu) { convictGpu('silent'); return; }
            onWorkerDeath(rec, { message: 'no answer for ' + Math.round(ms / 1000) + ' s', silent: true });
        }, ms);
    }
    function onWorkerMessage(rec, m) {
        if (m.type === 'verdict') { onVerdict(m); return; }
        if (m.type === 'note') return;
        // the card went away under this worker (mm-worker.js answers for the run ORT abandoned) — while
        // it was starting that is the start's failure (ensure() steps down); after it, the card is out
        if (m.type === 'error' && rec.gpu && GPU_GONE[m.code] && !rec.init) { convictGpu(GPU_GONE[m.code], m.message); return; }
        if (m.type === 'ready' || (m.type === 'error' && m.id == null && rec.init)) {
            var cb = rec.init; rec.init = null;
            if (!cb) return;
            if (m.type === 'ready') { rec.ready = true; cb.resolve(m); } else cb.reject(codedError(m));
            pump();
            return;
        }
        var t = rec.busy;
        if (!t || (m.id != null && m.id !== t.id)) return;
        rec.busy = null; disarm(rec);
        if (m.type !== 'error' && m.ms > 0) learn(m.type, m.ms);
        if (!t.settled) { t.settled = true; if (m.type === 'error') t.reject(codedError(m)); else t.resolve(m); }
        // the answer's own continuation usually queues that song's NEXT work (its chunks after its
        // log-mel): hand the worker out after it has run, or a lower-priority chunk slips in first
        // (caught by the scheduler test — on a phone that is a minute of someone else's song)
        schedulePump();
    }
    var pumpSoon = null;
    function schedulePump() { if (pumpSoon) return; pumpSoon = setTimeout(function () { pumpSoon = null; pump(); }, 0); }

    // ── how long the queued listening will take, from what THIS device took per task ──────────
    // The page used to guess from progress events: chunks arrive two at a time every ~14 s on a slow
    // phone, and a chunk already under way when a song was picked again finished «too fast» — the
    // promise came out at 0.66 of the truth (stand of 27.09). The workers time every task themselves.
    // Slower is believed at once, faster slowly: a phone heating up (or a tab sent to the background)
    // makes the next chunks longer, and a promise that lags a slowdown reads «≈ 1 мин» for three
    // (stand of 27.09 on a machine that slowed down mid-run: 20-s chunks became 45-s ones).
    var took = {};
    function learn(type, ms) { var p = took[type]; took[type] = p == null ? ms : ms > p ? p * 0.4 + ms * 0.6 : p * 0.85 + ms * 0.15; }
    /**
     * Seconds left for songs `[{ done, of, cut }]` (chunks done, chunks in all, whether its log-mel is
     * cut — `of` known). null until a chunk has been timed on this device: no number rather than a guess.
     */
    function estimate(songs) {
        var chunk = took.chunk; if (!chunk) return null;
        var live = pool.filter(function (r) { return r.session && r.ready && !r.dead; });
        var par = Math.max(1, Math.min(maxWorkers(), live.length || 1));
        // the pool played forward: a worker holding a chunk is free when that chunk's time is up (so the
        // countdown moves between two answers), every chunk still queued goes to whichever is free first
        // — the last chunk cannot be split between two workers, which «chunks ÷ workers» pretended
        var now = performance.now(), free = [], inflight = 0;
        live.forEach(function (r) { if (r.busy && r.busy.type === 'chunk') { inflight++; free.push(Math.max(chunk * 0.05, chunk - (now - (r.busy.sentAt || now)))); } });
        while (free.length < par) free.push(0);
        var left = 0, tail = 0;
        (songs || []).forEach(function (s) {
            left += Math.max(0, (s.of || 0) - (s.done || 0));
            if (!s.cut) tail += took.mel || 0;
            tail += (took.fit || 0) + (took.mixFeat || 0);
        });
        for (var q = Math.max(0, left - inflight); q > 0; q--) { free.sort(function (a, b) { return a - b; }); free[0] += chunk; }
        // a song's key and grid come after its last chunk, one after another: added, not shared out
        return Math.max(0, Math.round((Math.max.apply(null, free) + tail) / 1000));
    }
    /**
     * The card is out for the life of this page: every worker goes, everything queued or running is
     * rejected `model_switched`, and each song in work runs again on the processor (beatsOf). A card
     * lost or silent UNDER A SONG is also remembered for 30 days — a slow start is not (it is measured
     * again next time), a stray rejection is not (a guess costs the card one page), and a wrong
     * answer already is (onVerdict).
     */
    function convictGpu(why, detail) {
        if (gpuOff && !pool.some(function (r) { return r.gpu && !r.dead; })) return;
        gpuOff = true;
        if (why === 'lost' || why === 'silent') { try { root.OrtEpVerify && root.OrtEpVerify.remember && root.OrtEpVerify.remember(REJ_KEY, 'webgpu', { kind: why }); } catch (_) {} }
        tell('mm_beat_gpu_out', { why: why, songs: songsInWork, msg: detail ? String(detail).slice(0, 80) : undefined });
        release('gpu_' + why);
    }
    function onWorkerDeath(rec, e) {
        if (rec.dead) return;
        rec.dead = true; disarm(rec);
        try { rec.w.terminate(); } catch (_) {}
        pool = pool.filter(function (r) { return r !== rec; });
        var err = Object.assign(new Error('analysis worker died: ' + (e && e.message || 'unknown')), { code: e && e.silent ? 'worker_silent' : 'worker_died' });
        if (rec.init) { var cb = rec.init; rec.init = null; cb.reject(err); }
        var t = rec.busy; rec.busy = null;
        // a chunk the dead worker held goes back in the queue once — another worker, or a fresh
        // model, takes it; a second death of the same chunk is the chunk's answer. Only work that
        // still has its input can go again (chunks travel as copies; a song's samples are handed over)
        // (a task that MAKES its input when sent can make it again)
        if (t && !t.settled) {
            if (t.tries < 1 && (t.remake || !t.transfer.length)) { t.tries++; if (t.remake) { t.make = t.remake; t.msg = null; t.transfer = []; } queue.push(t); }
            else { t.settled = true; t.reject(err); }
        }
        // the model is gone with a worker that HAD it; a worker dying during its own start is the
        // start's failure (ensure() handles it — resetting `ready` here broke the GPU → WASM step)
        if (rec.kind === 'primary' && rec.ready) { modelOk = false; ready = null; }
        if (rec.kind === 'extra' && !rec.ready) extraFails++;
        pump();
    }

    // ── the scheduler ────────────────────────────────────────────────────────
    function order(a, b) { return (a.job.prio - b.job.prio) || (a.job.seq - b.job.seq) || (a.seq - b.seq); }
    function send(rec, t) {
        // a task whose input is a whole song's mono copy (mixFeat, vocalFeat, bandFeat) makes it NOW,
        // when a worker takes it: made at the call, the copies of every song waiting in the queue lived
        // side by side (57 MB per 5-min song at 48 kHz)
        if (t.make) {
            var mk = null;
            try { mk = t.make(); } catch (e) { t.settled = true; t.reject(e); schedulePump(); return; }
            t.make = null; t.msg = mk.msg; t.transfer = mk.transfer || [];
        }
        rec.busy = t; t.sent = true; t.sentAt = performance.now();
        var msg = Object.assign({ type: t.type, id: t.id }, t.msg);
        try { rec.w.postMessage(msg, t.transfer); armQuiet(rec, t); }
        catch (e) { rec.busy = null; t.settled = true; t.reject(e); }
    }
    /** The model the pool runs now (its ready workers say it) — a song's chunks are all answered by ONE model. */
    function modelKey() { for (var i = 0; i < pool.length; i++) { var r = pool[i]; if (r.session && r.ready && !r.dead && r.model) return r.model; } return null; }
    function pump() {
        if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
        // the model came back as ANOTHER one (the card's worker died and its second start stepped down
        // to the processor): a song cut for the first model is not finished by the second — it runs again
        var mk = modelKey();
        if (mk) queue.forEach(function (t) { if (!t.settled && t.model && t.model !== mk) { t.settled = true; t.reject(switched()); } });
        queue = queue.filter(function (t) { return !t.settled; });
        queue.sort(order);
        pool.forEach(function (rec) {
            if (rec.dead || rec.busy || !rec.ready) return;
            for (var i = 0; i < queue.length; i++) {
                var t = queue[i];
                if (t.session && !rec.session) continue;
                queue.splice(i, 1); send(rec, t); return;
            }
        });
        grow();
        // a song between two steps (its samples resampling, its chunks being cut) is still work
        if (!queue.length && !songsInWork && !pool.some(function (r) { return r.busy || r.init; })) armIdle();
    }
    /** More workers for chunks waiting on a ready model; a bare one for DSP work when there is none. */
    function grow() {
        var live = pool.filter(function (r) { return !r.dead; });
        var needDsp = queue.some(function (t) { return !t.session; });
        var needModel = queue.some(function (t) { return t.session; });
        // chunks waiting and no worker holds the model (released while idle, or its worker died):
        // bring the model back rather than leave them waiting for a worker that will never come
        if (needModel && !ready && !live.some(function (r) { return r.session; })) {
            ensure().catch(function (e) { queue.forEach(function (t) { if (t.session && !t.settled) { t.settled = true; t.reject(e); } }); pump(); });
            return;
        }
        // a bare worker needs no start and says nothing when it is up: hand the work out now (without
        // this the build's features, queued after the listening pool was released, waited forever)
        if (needDsp && !live.length) { try { spawn('bare'); schedulePump(); } catch (_) {} return; }
        // a device whose extra workers keep failing to start runs on the one it has
        if (!modelOk || !modelBytes || !readyInfo || readyInfo.provider !== 'wasm' || extraFails >= 2) return;
        // parked songs never grow the pool: they only use what is idle anyway
        var want = queue.filter(function (t) { return t.session && t.job.prio < PARKED; }).length;
        var sessions = live.filter(function (r) { return r.session; });
        var idle = sessions.filter(function (r) { return r.ready && !r.busy; }).length;
        var starting = sessions.filter(function (r) { return !r.ready; }).length;
        var room = maxWorkers() - sessions.length;
        for (var k = 0; k < Math.min(room, want - idle - starting); k++) spawnExtra();
    }
    function spawnExtra() {
        if (!root.__ortBuild) return;
        var rec;
        try { rec = spawn('extra', 'small0/wasm'); } catch (_) { return; }
        // an extra worker only ever runs the CPU provider: the plain build (half the JSEP wasm) — or
        // ORT 1.18, when the first worker already learned that this browser needs it
        var ob = ortFor(true);
        // an extra worker whose start never ends is let go like one whose start failed (the pool then
        // runs on the workers it has: extraFails)
        var cancel = afterVisible(function () { var cb = rec.init; if (!cb || rec.dead) return; rec.init = null; cb.reject(); }, readyBudget('wasm'));
        rec.init = { resolve: function () { cancel(); }, reject: function () { cancel(); onWorkerDeath(rec, { message: 'extra init failed' }); } };
        rec.w.postMessage({ type: 'init', ortUrl: ob.url, wasmBase: ob.wasmBase, ortFallbackUrl: ob.fallback, fallbackWasmBase: ob.fallback.replace(/[^/]+$/, ''),
            modelBytes: modelBytes.slice(0), providers: ['wasm'], verify: false });
    }
    /**
     * Which ORT a worker of this engine loads — ONE owner, window.__ortBuild (device-capabilities.js).
     * The current build imports a SHARED 4 GiB memory and has no wasm without SIMD; a browser that
     * cannot reserve that memory (32-bit Chrome and Yandex on Android phones under 8 GB, Safari ≤ 17)
     * or has no wasm SIMD (processors without SSE4.1) gets ORT 1.18 instead — small0 creates and
     * answers the known rhythm there (tests/playwright/ort-noshare-lane.mjs, case `beats`).
     * Until 12.10.2026 this engine asked for the current build only: /audio-to-notes, 30 days, 80
     * of ~1 100 starts on the processor died at once (25 without SIMD, the rest the reservation) and
     * the bars fell back to the drifting fixed grid — refund request #43 among them.
     * `legacy` is what the start ran on: '' | 'nosimd' | 'noshare' | 'forced'.
     */
    function ortFor(cpuOnly) {
        var B = root.__ortBuild, o = cpuOnly ? { cpuOnly: true } : undefined;
        var abs = function (u) { try { return new URL(u, root.location.href).href; } catch (_) { return u; } };
        if (typeof B.pick === 'function') { var p = B.pick(o); return { url: abs(p.url), wasmBase: abs(p.wasmBase), fallback: p.fallbackUrl, legacy: p.legacy || '' }; }
        return { url: B.url(o), wasmBase: B.wasmBase(), fallback: B.fallback(o), legacy: '' };
    }
    /** ORT's own words from a failed start: the ladder keeps them on the verdict of the provider that threw. */
    function ortCause(e) {
        var v = e && e.verdicts;
        if (v && v.length) for (var i = 0; i < v.length; i++) if (v[i] && v[i].error) return new Error(v[i].error);
        return e;
    }
    /**
     * small0 on the processor. A start the owner recognises as «this browser cannot run the current
     * build» marks the browser (initFailed) and runs ONCE more in a fresh worker after a pause — on
     * ORT 1.18 now: a failed initWasm is remembered by the runtime that failed, so never in the same one.
     */
    function initWasm(alive) {
        return initWith('small0', ['wasm']).catch(function (e) {
            var B = root.__ortBuild;
            if (!alive() || !B || typeof B.initFailed !== 'function' || !B.initFailed(ortCause(e), { legacy: e && e.ortLegacy || '', from: 'mm-beats' })) throw e;
            tell('mm_beat_ort_stepdown', { from: e && e.ortLegacy || 'current' });
            return new Promise(function (r) { setTimeout(r, 1000); }).then(function () {
                if (!alive()) throw abortError();
                return initWith('small0', ['wasm']);
            });
        });
    }
    function armIdle() {
        if (!pool.length) return;
        // the build is the memory-hungry part: a phone gets its half a gigabyte back sooner
        idleTimer = setTimeout(function () {
            idleTimer = null;
            if (queue.length || pool.some(function (r) { return r.busy || r.init; })) return;
            release('idle');
        }, lowMemory() ? 12000 : 45000);
    }

    // who wants to hear about the model download (the page paints «12 of 41 MB» in the slots)
    var progressFns = [];
    function onModelProgress(fn) { if (typeof fn === 'function') progressFns.push(fn); }
    function reportProgress(got, total, which) { progressFns.forEach(function (fn) { try { fn(got, total, which); } catch (_) {} }); }
    var dlInfo = null;

    /**
     * The model through the site's resumable downloader (js/model-download.js — Range resume after
     * a dropped mobile connection, a silence watchdog, the truncated-response cache purge).
     * Primary, then the fallback the blade prints when it is a DIFFERENT url (intl: tembrica.com,
     * then Beget; RU: both Beget, one try).
     */
    async function fetchModel(which) {
        var url = meta('mm-beat-' + which + '-url'), fb = meta('mm-beat-' + which + '-url-fallback');
        var tries = [url, fb].filter(function (u, i, a) { return !!u && a.indexOf(u) === i; }), lastErr = null;
        var t0 = performance.now();
        for (var i = 0; i < tries.length; i++) {
            try {
                var bytes;
                if (typeof root.__mdFetchBytes === 'function') {
                    var u8 = await root.__mdFetchBytes(tries[i], { onProgress: function (got, total) { reportProgress(got, total, which); } });
                    bytes = u8.byteLength === u8.buffer.byteLength ? u8.buffer : u8.slice().buffer;
                } else {
                    var r = await fetch(tries[i], { credentials: 'omit' });
                    if (!r.ok) throw new Error('model_fetch ' + r.status);
                    bytes = await r.arrayBuffer();
                }
                dlInfo = { src: i, ms: Math.round(performance.now() - t0), mb: Math.round(bytes.byteLength / 1e5) / 10 };
                return bytes;
            } catch (e) {
                lastErr = e;
                if (i + 1 < tries.length) { try { root.__aeFeature && root.__aeFeature('mm_beat_model_fallback', { m: which, err: String(e && e.message || e).slice(0, 80) }); } catch (_) {} }
            }
        }
        throw Object.assign(lastErr || new Error('model_fetch'), { code: 'model_fetch' });
    }

    function initWith(which, providers) {
        // the WASM model stays on the page (5 MB): the pool's other workers and a re-start after an
        // idle release take it from here instead of the network
        var have = which === 'small0' && modelBytes ? Promise.resolve(modelBytes.slice(0)) : fetchModel(which);
        return have.then(function (bytes) {
            if (which === 'small0' && !modelBytes) modelBytes = bytes.slice(0);
            // which ORT build this browser gets has ONE owner (device-capabilities.js): a hard-coded
            // JSEP build is the WebKit tab kill (onnxruntime#26827, 1 GB → 14 GB)
            var build = root.__ortBuild;
            if (!build) throw Object.assign(new Error('ort_build_missing'), { code: 'ort_build_missing' });
            // a worker that will only run the CPU provider takes the plain build: 12 MB of wasm
            // against the JSEP build's 25 — a phone downloads and compiles half, and the worker holds
            // ~70 MB less of the tab (measured 2026-09-30); the graphics-card worker keeps JSEP
            var cpuOnly = providers.length === 1 && providers[0] === 'wasm';
            var gpu = providers[0] === 'webgpu';
            var rec = spawn('primary', which + '/' + providers[0]), ob = ortFor(cpuOnly), t0 = activeNow();
            return new Promise(function (resolve, reject) {
                var cancel = null, stop = function () { if (cancel) { var c = cancel; cancel = null; c(); } };
                // `ortLegacy` — what this start ran on: initFailed() never retries a legacy start
                rec.init = { resolve: function (m) { stop(); resolve(m); }, reject: function (e) { stop(); onWorkerDeath(rec, e); if (e && typeof e === 'object') e.ortLegacy = ob.legacy; reject(e); } };
                rec.w.postMessage({ type: 'init', ortUrl: ob.url, wasmBase: ob.wasmBase, ortFallbackUrl: ob.fallback,
                    fallbackWasmBase: ob.fallback.replace(/[^/]+$/, ''), modelBytes: bytes, providers: providers }, [bytes]);
                // a start has a deadline (READY): a card that is slow to start is slower than the processor
                // on the song too and is where the card gets lost; a processor start that never ends is a hang
                var budget = readyBudget(providers[0]);
                cancel = (gpu ? afterActive : afterVisible)(function () {
                    cancel = null;
                    var cb = rec.init; if (!cb || rec.dead) return;
                    rec.init = null;
                    cb.reject(Object.assign(new Error((gpu ? 'the card' : 'the model') + ' was not ready in ' + Math.round(budget / 1000) + ' s'), { code: gpu ? 'gpu_slow' : 'init_timeout', ms: budget }));
                }, budget);
            }).then(function (m) { return { provider: m.provider, model: which, unverified: !!m.unverified, initMs: Math.round(activeNow() - t0), ort: ob.legacy }; });
        });
    }

    /**
     * One model per pool life; a GPU that fails the ladder falls back to small0 on WASM in a fresh worker.
     *
     * A PHONE goes to small0 on WASM straight away, graphics card or not. Measured on the field
     * (RU, 25–27.09.2026, phones that picked a file): straight to WASM — 12 of 14 reached the
     * numbers, the model ready in 7 s; final0 on the phone's card — 11 of 18, ready in 49 s (18 s of
     * it the 41 MB over a mobile line); the card failing its known-answer check after that download —
     * 1 of 7, 140 s; and 12 more phones with a card left before the model was ready at all. A phone's
     * card does not buy speed either: songs took a median 48 s there against 30 s on WASM.
     */
    function gpuFirst() {
        var ok = !gpuOff && !!(root.navigator && root.navigator.gpu) && !isWebKit() && !isPhone() && adapterSeen !== false;
        // ORT 1.18 (the browser's lane, ortFor) has no WebGPU provider
        try { if (ok && root.__ortBuild && typeof root.__ortBuild.legacy === 'function' && root.__ortBuild.legacy()) ok = false; } catch (_) {}
        try { if (ok && root.OrtEpVerify && root.OrtEpVerify.isRejected && root.OrtEpVerify.isRejected(REJ_KEY, 'webgpu')) ok = false; } catch (_) {}
        return ok;
    }
    /**
     * «navigator.gpu is there» is not «there is a card to run on»: a browser whose GPU is blocklisted,
     * a VM, a phone in «desktop site» mode answer requestAdapter() with null — and the 41 MB final0 was
     * downloaded before the worker found that out (bug report 2026-09-28: Chrome in desktop mode on an
     * Android phone, 4G at 1.45 Mbit/s — minutes of download for a model it could not run). Asked once,
     * before any download; no answer in 3 s is a no.
     */
    var adapterP = null, adapterSeen = null;
    function hasAdapter() {
        if (!adapterP) {
            adapterP = Promise.race([
                Promise.resolve().then(function () { return root.navigator.gpu.requestAdapter(); }).then(function (a) { return !!a; }, function () { return false; }),
                new Promise(function (r) { setTimeout(function () { r(false); }, 3000); })
            ]).then(function (v) { adapterSeen = v; return v; });
        }
        return adapterP;
    }
    function ensure() {
        if (ready) return ready;
        var gpuOk = gpuFirst();
        var t0 = performance.now(), again = !!readyInfo;
        var alive = function () { return ready === p; };
        var p = ready = (gpuOk ? hasAdapter() : Promise.resolve(false)).then(function (card) {
            if (!card) return initWasm(alive);
            return initWith('final0', ['webgpu']).catch(function (e) {
                // the pool was released under this start (the page asked, or the card was taken out):
                // that is not the card failing to start, and a dead chain does not start another model
                if (ready !== p) throw e;
                // a card that did not start is not asked again on this page: the next song (the pool is
                // released while idle) would pay for the same start to get the same answer
                gpuOff = true;
                tell('mm_beat_gpu_fallback', { code: e && e.code || null, ms: e && e.ms || undefined });
                // the card's start failed on the reservation the processor's start needs too: the owner
                // learns it here, and the processor starts on ORT 1.18 at once (every reservation tried
                // and refused is address space the browser does not give back)
                try { if (root.__ortBuild && root.__ortBuild.initFailed) root.__ortBuild.initFailed(ortCause(e), { legacy: e && e.ortLegacy || '', from: 'mm-beats-gpu' }); } catch (_) {}
                pool.filter(function (r) { return r.kind === 'primary'; }).forEach(function (r) { onWorkerDeath(r, { message: 'gpu fallback' }); });
                return initWasm(alive);
            });
        }).then(function (info) {
            if (ready !== p) return info;
            readyInfo = info; modelOk = true; info.ms = Math.round(performance.now() - t0);
            tell('mm_beat_ready', { p: info.provider, m: info.model, ms: info.ms, init_ms: info.initMs, dl_ms: dlInfo && dlInfo.ms, dl_src: dlInfo && dlInfo.src, mb: dlInfo && dlInfo.mb, again: again ? 1 : 0, max_w: maxWorkers(), ort: info.ort || undefined });
            pump();
            return info;
        }, function (e) { if (ready === p) ready = null; throw e; });
        return p;
    }

    /**
     * Terminates every worker (wasm memory back) — queued and running work is rejected: AbortError
     * when the page asked, `model_switched` when the graphics card is taken out (it answered wrong,
     * went silent or was lost — convictGpu): every song in work then runs again on WASM (beatsOf
     * does it for the beats; the mashup engine restarts a song's whole analysis once on that code).
     */
    function release(why) {
        if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
        var had = pool.length, reason = function () { return /^gpu_/.test(why || '') ? switched() : abortError(); };
        pool.forEach(function (r) {
            r.dead = true; disarm(r);
            try { r.w.terminate(); } catch (_) {}
            if (r.init) { var cb = r.init; r.init = null; try { cb.reject(reason()); } catch (_) {} }
            if (r.busy && !r.busy.settled) { r.busy.settled = true; r.busy.reject(reason()); }
        });
        pool = [];
        queue.forEach(function (t) { if (!t.settled) { t.settled = true; t.reject(reason()); } });
        queue = [];
        ready = null; modelOk = false;
        if (had && why) { try { root.__aeFeature && root.__aeFeature('mm_pool_release', { why: why, n: had }); } catch (_) {} }
    }

    // ONE song's 22 kHz samples are made at a time: the resample holds the song's mono copy twice for
    // a moment (the render and the copy out of it), and three decks landing together made three at once
    var prepTail = Promise.resolve();
    function prep(fn) { var p = prepTail.then(fn); prepTail = p.then(function () {}, function () {}); return p; }

    /** Mono 22 050 Hz by the browser's own resampler (off the main thread, band-limited). */
    function to22k(ab) {
        var OC = root.OfflineAudioContext || root.webkitOfflineAudioContext;
        var len = Math.max(1, Math.ceil(ab.duration * 22050));
        var off = new OC(1, len, 22050);
        var src = off.createBufferSource(); src.buffer = ab; src.connect(off.destination); src.start();
        return off.startRendering().then(function (r) { return new Float32Array(r.getChannelData(0)); });
    }
    function monoCopy(ab) {
        var n = ab.length, out = new Float32Array(n), ch = ab.numberOfChannels, g = 1 / ch;
        for (var c = 0; c < ch; c++) { var d = ab.getChannelData(c); for (var i = 0; i < n; i++) out[i] += d[i] * g; }
        return out;
    }

    /** beat_this split_predict_aggregate: chunks with BORDER frames either side; the last one ends at T. */
    function chunkStarts(T, chunk) {
        var starts = [];
        for (var st = -BORDER; st < T - BORDER; st += chunk - 2 * BORDER) starts.push(st);
        if (T > chunk - 2 * BORDER) starts[starts.length - 1] = T - (chunk - BORDER);
        return starts;
    }
    function framesFor(sec) { return 1 + Math.floor(Math.max(0, sec) * 22050 / 441); }
    function chunksFor(sec) {
        var gpu = !!(readyInfo && readyInfo.provider !== 'wasm');
        return chunkStarts(framesFor(sec), gpu ? CHUNK_GPU : CHUNK_WASM).length;
    }

    async function beatsOf(task, j, ab, o) {
        songsInWork++;
        try {
            for (var attempt = 0; ; attempt++) {
                try {
                    var grid = await beatsSteps(task, j, ab, o);
                    if (attempt) grid.again = attempt;
                    return grid;
                } catch (e) {
                    // the model under this song changed — its card answered wrong, went silent or was lost:
                    // the song runs again, once, on what the pool runs now. The caller waited for one song.
                    if (e && e.code === 'model_switched' && attempt < 1 && !j.cancelled) continue;
                    throw e;
                }
            }
        } finally { songsInWork--; pump(); }
    }
    async function beatsSteps(task, j, ab, o) {
        var t0 = performance.now();
        var info = await ensure();
        if (j.cancelled) throw abortError();
        var pcm = await prep(function () { if (j.cancelled) throw abortError(); return to22k(ab); });
        if (j.cancelled) throw abortError();
        var mel = await task('mel', { pcm: pcm }, [pcm.buffer], false);
        var T = mel.frames, M = mel.data, melMs = mel.ms, gpu = info.provider !== 'wasm';
        // every chunk of this song belongs to the model the song was cut for (pump() holds it to that)
        var mkey = info.model + '/' + info.provider;
        mel = null;
        var starts = chunkStarts(T, gpu ? CHUNK_GPU : CHUNK_WASM), n = starts.length, outs = new Array(n), done = 0, busyMs = 0, widest = 0;
        var tModel = performance.now(), firstAt = 0;
        if (o.onProgress) o.onProgress(0, n);
        try {
            await Promise.all(starts.map(function (st, ci) {
                var len = Math.min(gpu ? CHUNK_GPU : CHUNK_WASM, T + BORDER - st), x = new Float32Array(len * NMEL);
                for (var f = 0; f < len; f++) { var g = st + f; if (g >= 0 && g < T) x.set(M.subarray(g * NMEL, g * NMEL + NMEL), f * NMEL); }
                // copied, not handed over: a chunk whose worker dies goes again with its input intact
                return task('chunk', { x: x, len: len }, [], true, mkey).then(function (r) {
                    outs[ci] = r; done++; busyMs += r.ms || 0; if (!firstAt) firstAt = performance.now();
                    widest = Math.max(widest, pool.filter(function (p) { return p.session && !p.dead; }).length);
                    if (o.onProgress) o.onProgress(done, n);
                });
            }));
            M = null;
            // keep_first: the later chunk is written first and the earlier one overwrites the overlap —
            // exactly the order the worker used to run them in, so the logits are the same numbers
            var beat = new Float32Array(T).fill(-1000), down = new Float32Array(T).fill(-1000);
            for (var ci = n - 1; ci >= 0; ci--) {
                var st = starts[ci], r = outs[ci], len = r.beat.length;
                for (var f = BORDER; f < len - BORDER; f++) { var g = st + f; if (g >= 0 && g < T) { beat[g] = r.beat[f]; down[g] = r.down[f]; } }
            }
            outs = null;
            if (gpu) {
                // the card answers the known rhythm again after every song (same worker: one card)
                var kv = await task('kat', {}, [], true, mkey);
                if (!kv.verdict || !kv.verdict.ok) throw Object.assign(new Error('provider failed the known-answer test after the song'), { code: 'gpu_output_corrupt' });
            }
            var modelMs = Math.round(performance.now() - tModel);
            var acts = o.keepActs ? { beat: beat, down: down } : null;
            var fit = await task('fit', { beat: beat, down: down }, [], false);
            return Object.assign(fit.grid, { ms: Math.round(performance.now() - t0), melMs: melMs, provider: info.provider, acts: acts,
                chunks: n, chunkMs: Math.round(busyMs / Math.max(1, n)), modelMs: modelMs, workers: Math.max(1, widest), firstMs: firstAt ? Math.round(firstAt - tModel) : null });
        } catch (e) {
            // a card that stopped answering right is remembered (onVerdict); everything it touched is
            // dropped and every song in work — this one too — runs again on WASM (beatsOf)
            if (e && e.code === 'gpu_output_corrupt') {
                convictGpu('corrupt');
                throw switched();
            }
            throw e;
        }
    }

    function job(opts) {
        opts = opts || {};
        var j = { seq: ++jobSeq, prio: opts.prio != null ? opts.prio : 1, cancelled: false, parked: false, tasks: [], parkTimer: null };
        j.base = j.prio;
        // `payload` is the message, or a function → { msg, transfer } called when a worker takes the task;
        // `model` — the model a session task was cut for ('final0/webgpu'): no other model answers it
        function task(type, payload, transfer, needSession, model) {
            if (j.cancelled) return Promise.reject(abortError());
            return new Promise(function (resolve, reject) {
                var lazy = typeof payload === 'function';
                var t = { id: ++taskSeq, seq: taskSeq, job: j, type: type, msg: lazy ? null : payload, make: lazy ? payload : null, remake: lazy ? payload : null, transfer: transfer || [], session: !!needSession,
                    model: needSession && model || null, resolve: resolve, reject: reject, settled: false, sent: false, tries: 0 };
                j.tasks = j.tasks.filter(function (x) { return !x.settled; });
                j.tasks.push(t); queue.push(t); pump();
            });
        }
        var api = {
            beats: function (ab, o) { return beatsOf(task, j, ab, o || {}); },
            mixFeat: function (ab) { return task('mixFeat', function () { var m = monoCopy(ab); return { msg: { mono: m, sr: ab.sampleRate }, transfer: [m.buffer] }; }, null, false); },
            vocalFeat: function (ab, tuningCents, phrasesOnly) { return task('vocalFeat', function () { var m = monoCopy(ab); return { msg: { mono: m, sr: ab.sampleRate, tuningCents: tuningCents || 0, phrasesOnly: !!phrasesOnly }, transfer: [m.buffer] }; }, null, false); },
            bandFeat: function (bassAb, otherAb) { return task('bandFeat', function () { var b = monoCopy(bassAb), ot = monoCopy(otherAb); return { msg: { bass: b, other: ot, sr: bassAb.sampleRate }, transfer: [b.buffer, ot.buffer] }; }, null, false); },
            park: function () {
                if (j.cancelled || j.parked) return;
                j.parked = true; j.prio = PARKED;
                clearTimeout(j.parkTimer); j.parkTimer = setTimeout(function () { if (j.parked) api.cancel(); }, PARK_MS);
                pump();
            },
            resume: function () {
                if (j.cancelled || !j.parked) return false;
                j.parked = false; j.prio = j.base; clearTimeout(j.parkTimer);
                pump();
                return true;
            },
            cancel: function () {
                if (j.cancelled) return;
                j.cancelled = true; clearTimeout(j.parkTimer);
                j.tasks.forEach(function (t) { if (!t.settled) { t.settled = true; t.reject(abortError()); } });
                j.tasks = [];
                pump();
            },
            state: function () { return j.cancelled ? 'cancelled' : j.parked ? 'parked' : 'live'; }
        };
        return api;
    }

    root.MashupAnalysis = {
        job: job, ensure: ensure, release: function () { release('asked'); }, onModelProgress: onModelProgress, chunksFor: chunksFor, estimate: estimate,
        configure: configure,
        info: function () { return readyInfo; },
        /** The beat model this device will download first — 'final0' (a computer's card) or 'small0'. */
        planned: function () { return readyInfo ? readyInfo.model : (gpuFirst() ? 'final0' : 'small0'); },
        // one-off jobs: the build's features (a person is watching its bar) and the old call shape
        beats: function (ab, o) { return job({ prio: o && o.prio != null ? o.prio : 1 }).beats(ab, o); },
        mixFeat: function (ab) { return job({ prio: 1 }).mixFeat(ab); },
        vocalFeat: function (ab, tc, po) { return job({ prio: 0 }).vocalFeat(ab, tc, po); },
        bandFeat: function (b, o) { return job({ prio: 0 }).bandFeat(b, o); },
        // tests and telemetry: what the pool is doing right now
        _state: function () { return { workers: pool.filter(function (r) { return !r.dead; }).map(function (r) { return { kind: r.kind, ready: r.ready, busy: r.busy ? r.busy.type : null, gpu: r.gpu }; }), queued: queue.length, max: maxWorkers(), model: modelOk, gpuOff: gpuOff }; }
    };
})(typeof window !== 'undefined' ? window : self);
